import {AfterViewInit, Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, ViewChild} from '@angular/core';
import {FormGroup, ReactiveFormsModule} from "@angular/forms";
import {IconBadgeComponent} from "../icon-badge/icon-badge.component";
import {DropDownListComponent, DropDownListModule, SortOrder} from "@syncfusion/ej2-angular-dropdowns";
import {FilteringEventArgs, PopupEventArgs} from "@syncfusion/ej2-dropdowns";
import {Subscription} from 'rxjs';

import {TYPE} from "../../types/types";
import {ChangeEventArgs} from '@syncfusion/ej2-angular-buttons';
import {AvatarComponent} from "../avatar/avatar.component";

@Component({
    selector: 'app-form-dropdown',
    imports: [
        IconBadgeComponent,
        DropDownListModule,
        ReactiveFormsModule,
        AvatarComponent
    ],
    templateUrl: './form-dropdown.component.html',
    styleUrl: './form-dropdown.component.scss'
})
export class FormDropdownComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {
  @ViewChild('dropdownRef', {static: false}) public dropdownRef!: DropDownListComponent;
  
  @Input() public fCN: string = '';
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  @Input() public dataSource: any[] = [];
  @Input() public mapping: TYPE.ExtendedFieldSettingsModel = {text: 'name', value: 'id'};
  @Input() public disabled: boolean = false;
  @Input() public cssClass: string = '';
  @Input() public fG: FormGroup = new FormGroup({});
  @Input() public placeholder: string = '';
  @Input() public icon: string = '';
  @Input() public imageField: string = '';
  @Input() public sortOrder: SortOrder = 'None';
  @Input() public marker: 'priority' | null = null;
  @Input() public withEmpty: boolean = false;
  @Input() public allowFiltering: boolean = false;
  @Input() public filterText: string = '';
  @Input() public returnObject: boolean = false;
  @Input() public popupWidth: string = '100%';
  @Input() public width: string = '';
  @Input() public errorText: string = '';
  @Input() public hasErrorField: boolean = false;
  @Output() public  valueChange: EventEmitter<string | number | TYPE.FieldSettingsModelWithId> = new EventEmitter<string | number | TYPE.FieldSettingsModelWithId>();
  @Output() public  reachedEndOfList: EventEmitter<void> = new EventEmitter<void>();
  @Output() public dropdownClosed: EventEmitter<void> = new EventEmitter<void>();
  // eslint-disable-next-line @angular-eslint/no-output-native
  @Output() public  search: EventEmitter<string> = new EventEmitter<string>();

  private popupScrollPosition: number = 0;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private popupElement: any = null;
  private pendingFilterEvent: FilteringEventArgs | null = null;
  private currentFilterText: string = '';
  private popupScrollHandler: (() => void) | null = null; //needed because when the dropdown opens popupElement.addEventListener('scroll', () => { always adds another scroll listener which we never remove  private currentFilterText: string = '';
  private controlSubscription?: Subscription;


  public ngOnInit(): void {
    this.currentFilterText = this.filterText;

    // Add empty option if needed
    if (this.withEmpty) {
      this.dataSource.unshift({id: '', name: this.placeholder});
    }
  }

  public ngOnChanges(): void {
    this.currentFilterText = this.filterText;

    // Restore scroll position if needed
    if (this.popupElement && this.popupScrollPosition > 0) {
      setTimeout(() => {
        this.popupElement.scrollTop = this.popupScrollPosition
      }, 5);
    }
    
    this.syncDropdownState();
    
    // If we have a pending filter event, update it with the new dataSource
    if (this.pendingFilterEvent) {
      setTimeout(() => {
        this.pendingFilterEvent!.updateData(this.dataSource);
        this.pendingFilterEvent = null;
      }, 0);
    }
  }

  public ngAfterViewInit(): void {
    this.subscribeToControlChanges();
    this.syncDropdownState();
  }

  public ngOnDestroy(): void {
    this.controlSubscription?.unsubscribe();
  }

  public onChange(event: ChangeEventArgs): void {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const tmp: TYPE.FieldSettingsModelWithId = (event as any).itemData as TYPE.FieldSettingsModelWithId;
    if (tmp && tmp.id) {
      if (this.returnObject) {
        this.valueChange.emit(tmp);
      } else {
        this.valueChange.emit(tmp.id);
      }
    }
  }

  public onClose(): void {
    if (this.popupElement && this.popupScrollHandler) {
      this.popupElement.removeEventListener(
        'scroll',
        this.popupScrollHandler
      );
    }

    this.popupScrollHandler = null;
    this.popupElement = null;
    this.popupScrollPosition = 0;
    this.pendingFilterEvent = null;

    this.dropdownClosed.emit();
  }

  private subscribeToControlChanges(): void {
    this.controlSubscription?.unsubscribe();
    const control = this.fG.controls[this.fCN];
    if (!control) {
      return;
    }

    this.controlSubscription = new Subscription();
    this.controlSubscription.add(control.valueChanges.subscribe(() => this.syncDropdownState()));
    this.controlSubscription.add(control.statusChanges.subscribe(() => this.syncDropdownState()));
  }

  private syncDropdownState(): void {
    if (!this.dropdownRef || !this.dataSource) {
      return;
    }

    setTimeout(() => {
      const control = this.fG.controls[this.fCN];
      if (!control || !this.dropdownRef) {
        return;
      }

      this.dropdownRef.dataSource = this.dataSource;
      this.dropdownRef.enabled = !this.disabled && !control.disabled;
      this.dropdownRef.value = control.value;
      this.dropdownRef.dataBind();
    }, 0);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public resolveDisplayItem(data: any | null): any | null {
    if (data) {
      return data;
    }

    const control = this.fG.controls[this.fCN];
    if (!control) {
      return null;
    }

    const valueField = (this.mapping?.value ?? 'id') as string;
    const selectedValue = control.value;
    return this.dataSource.find((item) => item?.[valueField] === selectedValue) ?? null;
  }

  public filter(event: FilteringEventArgs): void {
    // Prevent default filtering, we'll handle it ourselves
    event.preventDefaultAction = true;
    this.currentFilterText = event.text;
    
    // Store the event so we can update it when dataSource changes
    this.pendingFilterEvent = event;
    
    // Emit search event to parent - parent will update dataSource, triggering ngOnChanges
    this.search.emit(event.text);
  }

  public checkEndOfList(event: PopupEventArgs): void {
    const popupElement = event.popup.element.querySelector(
      'div.e-content.e-dropdownbase'
    ) as HTMLElement | null;

    if (!popupElement) {
      return;
    }

    if (this.popupElement && this.popupScrollHandler) {
      this.popupElement.removeEventListener(
        'scroll',
        this.popupScrollHandler
      );
    }

    this.popupElement = popupElement;
    this.restoreFilterInputValue(event);

    this.popupScrollHandler = () => {
      const {scrollTop, scrollHeight, clientHeight} = popupElement;

      if (scrollTop + clientHeight >= scrollHeight) {
        this.popupScrollPosition = scrollHeight - clientHeight;
        this.reachedEndOfList.emit();
      }
    };

    popupElement.addEventListener('scroll', this.popupScrollHandler);
  }

  private restoreFilterInputValue(event: PopupEventArgs): void {
    if (!this.allowFiltering) {
      return;
    }

    const filterInput = event.popup.element.querySelector('input.e-input-filter') as HTMLInputElement | null;
    if (filterInput) {
      filterInput.value = this.currentFilterText;
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public getItemVisual(data: any | null): { kind: 'image' | 'icon'; value: string } | null {
    if (!data) return null;
    const m = this.mapping as TYPE.ExtendedFieldSettingsModel;

    // Prefer image when both exist
    if (m.image && typeof data[m.image] === 'string' && (data[m.image] as string).trim()) {
      return {kind: 'image', value: data[m.image] as string};
    }
    if (m.icon && typeof data[m.icon] === 'string' && (data[m.icon] as string).trim()) {
      return {kind: 'icon', value: data[m.icon] as string};
    }
    return null;
  }
}
